import { genericMemo } from "@redotech/react-util/component";
import { useHandler } from "@redotech/react-util/hook";
import { useEffect, useMemo, useState } from "react";
import { RedoFilterDropdownAnchor } from "../arbiter-components/filter-group/redo-filter-group";
import { RedoListItem } from "../arbiter-components/list/redo-list";
import { RedoMultiselectDropdown } from "../arbiter-components/list/redo-multiselect-dropdown";
import { Flex } from "../flex";
import { Text } from "../text";

function optionToItem<T>(
  option: T,
  optionToFriendlyName: (option: T) => string,
): RedoListItem<T> {
  return {
    id: `${option}`,
    value: option,
    type: "text",
    text: optionToFriendlyName(option),
  };
}

export const SimpleMultiSelectFilterDropdown = genericMemo(
  function SimpleMultiSelectFilterDropdown<T extends string>({
    values,
    setValues,
    options,
    optionToFriendlyName,
    selectedOptionsToDisplay,
    openOnRender,
    filterStyle = "value",
    searchPlaceholder,
  }: {
    values: T[] | undefined;
    setValues(items: T[]): void;
    options: T[];
    optionToFriendlyName: (option: T) => string;
    selectedOptionsToDisplay: (selectedOptions: T[] | undefined) => string;
    openOnRender?: boolean;
    filterStyle?: "query" | "value";
    searchPlaceholder?: string;
  }) {
    const [searchString, setSearchString] = useState<string>("");

    const optionItems: RedoListItem<T>[] = useMemo(() => {
      if (!searchString) {
        return options.map((option) =>
          optionToItem(option, optionToFriendlyName),
        );
      }

      const lowerCaseSearch = searchString.toLowerCase();
      return options
        .filter((option) =>
          optionToFriendlyName(option).toLowerCase().includes(lowerCaseSearch),
        )
        .map((option) => optionToItem(option, optionToFriendlyName));
    }, [options, optionToFriendlyName, searchString]);

    const selectedOptions = useMemo(() => {
      return (values || []).map((value) =>
        optionToItem(value, optionToFriendlyName),
      );
    }, [optionToFriendlyName, values]);

    const [dropdownButtonRef, setDropdownButtonRef] =
      useState<HTMLButtonElement | null>(null);

    const [dropdownOpen, setDropdownOpen] = useState(false);

    useEffect(() => {
      if (openOnRender && dropdownButtonRef) {
        dropdownButtonRef.click();
      }
    }, [openOnRender, dropdownButtonRef]);

    const handleDropdownOpenChange = useHandler((open: boolean) => {
      setDropdownOpen(open);
      if (!open) {
        setSearchString("");
      }
    });

    const selectOptions = useHandler((items: RedoListItem<T>[]) =>
      setValues(items.map((i) => i.value)),
    );

    const anyItemsSelected = values && values.length > 0;

    return (
      <>
        <RedoFilterDropdownAnchor
          color={filterStyle === "query" ? "secondary" : "primary"}
          onClick={() => handleDropdownOpenChange(!dropdownOpen)}
          ref={setDropdownButtonRef}
          text={selectedOptionsToDisplay(values)}
          tooltip={
            anyItemsSelected && (
              <Flex dir="column">
                {values.map((value) => (
                  <Text key={value}>{optionToFriendlyName(value)}</Text>
                ))}
              </Flex>
            )
          }
          weight={filterStyle === "query" ? "regular" : "medium"}
        />
        <RedoMultiselectDropdown<T>
          dropdownAnchor={dropdownButtonRef}
          dropdownOpen={dropdownOpen}
          fitToAnchor={false}
          items={optionItems}
          searchPlaceholder={searchPlaceholder || "Search options..."}
          searchString={searchString}
          selectedItems={selectedOptions}
          setDropdownOpen={handleDropdownOpenChange}
          setSearchString={setSearchString}
          setSelectedItems={selectOptions}
          size="xs"
        />
      </>
    );
  },
);
